import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button, Modal } from '@bynder/design-system';
import { useTranslate } from '@bynder/localization';
import modalContainer from 'packages/common/modalContainer';
import { sanitizePathname } from 'packages/helpers/navigation';
import { useDeferredClose } from '~/hooks/useDeferredClose';

const useDesignEditRestrictionModal = () => {
    const { translate } = useTranslate();
    const navigate = useNavigate();
    const [isOpen, setRestrictionModalOpen] = useState<boolean>(false);
    const [shouldRender, onCloseAnimationEnd] = useDeferredClose(isOpen);
    const [textData, setTextData] = useState({ title: '', description: '' });
    const [fixDesignLink, setFixDesignLink] = useState<string | null>(null);

    const handleClose = () => {
        setRestrictionModalOpen(false);
        setFixDesignLink(null);
    };

    const handleFixDesign = () => {
        if (fixDesignLink) {
            handleClose();
            navigate(sanitizePathname(fixDesignLink));
        }
    };

    const editDesignBtn = (
        <Button
            variant="primary"
            onClick={handleFixDesign}
            title={translate('modal.design.missing_assets.edit_design')}
        >
            {translate('modal.design.missing_assets.edit_design')}
        </Button>
    );

    const cancelBtn = (
        <Button variant="secondary" onClick={handleClose} title={translate('modal.design.missing_assets.cancel')}>
            {translate('modal.design.missing_assets.cancel')}
        </Button>
    );

    const okayBtn = (
        <Button variant="primary" onClick={handleClose} title={translate('modal.design.not_published.okay')}>
            {translate('modal.design.not_published.okay')}
        </Button>
    );

    const renderRestrictionModal = () => {
        if (!shouldRender) {
            return null;
        }

        return (
            <Modal
                container={modalContainer}
                title={textData.title}
                isOpen={isOpen}
                onCloseAnimationEnd={onCloseAnimationEnd}
                onClose={handleClose}
                actionPrimary={fixDesignLink ? editDesignBtn : okayBtn}
                actionSecondary={fixDesignLink && cancelBtn}
            >
                {textData.description}
            </Modal>
        );
    };

    return {
        setTextData,
        setRestrictionModalOpen,
        setFixDesignLink,
        renderRestrictionModal,
    };
};

export default useDesignEditRestrictionModal;
